import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle, CalendarCheck, FileText, UserCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { PHONE, PHONE_RAW } from "@/lib/api";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Tests & Prices", href: "#tests" },
  { label: "Packages", href: "#packages" },
  { label: "Corporate", href: "#corporate" },
  { label: "Why Hemozy", href: "#why" },
  { label: "FAQs", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -70 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header data-testid="header-nav" className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/80 shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[70px] flex items-center justify-between">
        <div className="flex items-center gap-8 lg:gap-12">
          <a href="#top" onClick={(e) => go(e, "#top")} className="shrink-0"><Logo /></a>

          <nav className="hidden lg:flex items-center gap-7 xl:gap-8" data-testid="desktop-nav-links">
            {LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={(e) => go(e, l.href)}
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${scrolled ? "text-slate-600 hover:text-[#D92638]" : "text-slate-300 hover:text-white"}`}>
                {l.label}
              </a>
            ))}
          </nav>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/reports" data-testid="my-report-link"
            className="flex items-center gap-2 text-sm font-bold rounded-full border-2 border-[#0D9488] text-[#0D9488] px-4 py-2 bg-white/95 hover:bg-[#0D9488] hover:text-white transition-all duration-200 active:scale-95">
            <FileText className="w-4 h-4" /> Get My Report
          </Link>
          <a href={`https://wa.me/${PHONE_RAW}?text=${encodeURIComponent("Hi Hemozy! I'd like to book a test.")}`} target="_blank" rel="noreferrer"
            data-testid="whatsapp-cta-button" aria-label="WhatsApp"
            className={`hidden xl:flex w-9 h-9 rounded-full items-center justify-center transition-colors ${scrolled ? "text-[#0D9488] hover:bg-[#0D9488]/10" : "text-[#14B8A6] hover:bg-white/10"}`}>
            <MessageCircle className="w-5 h-5" />
          </a>
          <a href={`tel:${PHONE_RAW}`} data-testid="call-cta-button" aria-label={`Call ${PHONE}`}
            className={`hidden xl:flex w-9 h-9 rounded-full items-center justify-center transition-colors ${scrolled ? "text-slate-600 hover:bg-slate-100" : "text-slate-300 hover:bg-white/10"}`}>
            <Phone className="w-5 h-5" />
          </a>
          <Link to="/login" data-testid="customer-login-button"
            className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${scrolled ? "text-slate-600 hover:text-[#0B192C]" : "text-slate-300 hover:text-white"}`}>
            <UserCircle2 className="w-5 h-5" /> Login
          </Link>
          <button onClick={() => onBook([])} data-testid="book-appointment-button"
            className="flex items-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 hover:-translate-y-0.5 active:scale-95 shadow-[0_6px_20px_-6px_rgba(217,38,56,0.5)]">
            <CalendarCheck className="w-4 h-4" /> Book Appointment
          </button>
        </div>

        <button className={`lg:hidden ${scrolled ? "text-[#0B192C]" : "text-white"}`} onClick={() => setOpen(!open)} data-testid="mobile-menu-button" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200 overflow-hidden" data-testid="mobile-menu">
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={(e) => go(e, l.href)}
                  className="py-2.5 text-base font-medium text-slate-700 hover:text-[#D92638] border-b border-slate-100">
                  {l.label}
                </a>
              ))}
              <Link to="/reports" data-testid="mobile-report-link"
                className="mt-3 flex items-center justify-center gap-2 border border-[#0D9488] text-[#0D9488] font-semibold px-5 py-3 rounded-full active:scale-95 transition-transform">
                <FileText className="w-4 h-4" /> Get My Report
              </Link>
              <Link to="/login" data-testid="mobile-login-link"
                className="mt-3 flex items-center justify-center gap-2 border border-slate-300 text-slate-700 font-semibold px-5 py-3 rounded-full active:scale-95 transition-transform">
                <UserCircle2 className="w-4 h-4" /> Login / Sign up
              </Link>
              <button onClick={() => { setOpen(false); onBook([]); }} data-testid="mobile-book-button"
                className="mt-3 flex items-center justify-center gap-2 bg-[#D92638] text-white font-semibold px-5 py-3 rounded-full active:scale-95 transition-transform">
                <CalendarCheck className="w-4 h-4" /> Book Appointment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
